import { useSelector } from "react-redux";
import { RootState } from "app/store";
import { PLAN_LIMITS, createUnlimitedQuotaStatus } from "./constants";
import { AccountLimits, SubscriptionState, PlanLimits, PlanType, QuotaStatus } from "./types";

/**
 * Subscription 状態全体を取得
 */
export const useSubscription = (): SubscriptionState => {
  return useSelector((state: RootState) => state.subscription);
};

/**
 * 現在のプランを取得
 */
export const usePlan = (): PlanType => {
  return useSelector((state: RootState) => state.subscription.plan);
};

/**
 * プラン制限値を取得（アクティブユーザーのlimitsを返す）
 */
export const usePlanLimits = (): PlanLimits => {
  const subscription = useSelector((state: RootState) => state.subscription);
  const activeUser = useSelector((state: RootState) => state.user.active);

  // subscriptionが未初期化の場合はデフォルト値を返す
  if (!subscription) {
    return PLAN_LIMITS[PlanType.TRIAL];
  }

  // アクティブユーザーのlimitsがある場合はそれを返す
  if (activeUser?.uid && subscription.playerLimits?.[activeUser.uid]) {
    return subscription.playerLimits[activeUser.uid];
  }

  // フォールバック: プランのデフォルト値（undefinedの場合はTRIALを使用）
  return PLAN_LIMITS[subscription.plan] ?? PLAN_LIMITS[PlanType.TRIAL];
};

/**
 * アカウント単位のプラン制限値を取得
 */
export const useAccountLimits = (): AccountLimits => {
  const accountLimits = useSelector((state: RootState) => state.subscription?.accountLimits);
  // フォールバック: デフォルト値
  return (
    accountLimits ?? {
      players: PLAN_LIMITS[PlanType.TRIAL].players,
      assets: PLAN_LIMITS[PlanType.TRIAL].assets,
      chatHistoryLimit: createUnlimitedQuotaStatus(),
    }
  );
};

/**
 * 素材制限値を取得（アクティブユーザーのassets limitsを返す）
 */
export const useAssetLimit = (): QuotaStatus => {
  const subscription = useSelector((state: RootState) => state.subscription);
  const activeUser = useSelector((state: RootState) => state.user.active);

  // subscriptionが未初期化の場合はデフォルト値を返す
  if (!subscription) {
    return PLAN_LIMITS[PlanType.TRIAL].assets;
  }

  // アクティブユーザーのassets limitsがある場合はそれを返す
  if (activeUser?.uid && subscription.playerLimits?.[activeUser.uid]) {
    return subscription.playerLimits[activeUser.uid].assets;
  }

  // フォールバック: アカウント単位のassets limits
  return subscription.accountLimits?.assets ?? PLAN_LIMITS[PlanType.TRIAL].assets;
};
